
import React, { useEffect, useState } from 'react';
import { NursingHomesList } from './NursingHomesList';
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface MultiLocationMapProps {
  address: string;
  apiKey: string;
  nursingHomes: any[];
  countySlug?: string;
  setHasError: (hasError: boolean) => void;
  width: string | number;
  height: string | number;
  className?: string;
  defaultExpanded?: boolean;
  allNursingHomes?: boolean;
}

export function MultiLocationMap({ 
  address,
  apiKey,
  nursingHomes = [],
  countySlug,
  setHasError,
  width,
  height,
  className = '',
  defaultExpanded = true,
  allNursingHomes = false
}: MultiLocationMapProps) {
  const [selectedHome, setSelectedHome] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const navigate = useNavigate();
  
  const formatAddressForMapUrl = (addr: string) => {
    return encodeURIComponent(addr.replace(/\s+/g, ' ').trim());
  };
  
  // Create a map URL that includes all nursing home locations
  const mapUrl = () => {
    try {
      // Base map with county location as center
      let baseUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${formatAddressForMapUrl(address)}&zoom=10&language=en&region=IE&maptype=roadmap`;
      
      // If we have nursing homes and a selected home, center on that home instead
      if (nursingHomes?.length > 0 && selectedHome) {
        const selectedHomeData = nursingHomes.find(home => home.nh_slug === selectedHome);
        if (selectedHomeData) {
          if (selectedHomeData.latitude && selectedHomeData.longitude) {
            baseUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&center=${selectedHomeData.latitude},${selectedHomeData.longitude}&q=${selectedHomeData.latitude},${selectedHomeData.longitude}&zoom=14&language=en&region=IE&maptype=roadmap`;
          } else if (selectedHomeData.standardized_address) {
            baseUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${formatAddressForMapUrl(selectedHomeData.standardized_address)}&zoom=14&language=en&region=IE&maptype=roadmap`;
          }
        }
      }
      
      // If we have nursing homes with addresses or coordinates, create a view with multiple markers
      if (nursingHomes?.length > 0) {
        const validHomes = nursingHomes.filter(home => 
          home.standardized_address || (home.latitude && home.longitude)
        );
        
        if (validHomes.length > 0) {
          // If all nursing homes view is enabled, adjust the query
          if (allNursingHomes) {
            baseUrl = `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=nursing+homes+in+Ireland&zoom=7&language=en&region=IE`;
          }
          // If a home is selected, center on it but still show all markers
          else if (selectedHome) {
            const selectedHomeData = validHomes.find(home => home.nh_slug === selectedHome);
            if (selectedHomeData) {
              // Create a map with multiple markers but centered on selected home
              if (selectedHomeData.latitude && selectedHomeData.longitude) {
                baseUrl = `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=nursing+homes+in+${formatAddressForMapUrl(address)}&center=${selectedHomeData.latitude},${selectedHomeData.longitude}&zoom=14&language=en&region=IE`;
              } else if (selectedHomeData.standardized_address) {
                baseUrl = `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=nursing+homes+in+${formatAddressForMapUrl(address)}&center=${formatAddressForMapUrl(selectedHomeData.standardized_address)}&zoom=14&language=en&region=IE`;
              }
            }
          } else {
            // Show all nursing homes in the county with markers
            baseUrl = `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=nursing+homes+in+${formatAddressForMapUrl(address)}&zoom=10&language=en&region=IE`;
          }
        }
      }
      
      return baseUrl;
    } catch (error) {
      console.error('Error generating map URL:', error);
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${formatAddressForMapUrl(address)}&zoom=10&language=en&region=IE&maptype=roadmap`;
    }
  };

  const handleMapError = () => {
    console.error('Error loading Google Map for county:', address);
    setHasError(true);
    toast.error("Couldn't load the map for this county");
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const handleHomeSelection = (nhSlug: string | null) => {
    setSelectedHome(nhSlug);
    
    // If the user clicks on a pin and we're viewing all nursing homes
    if (nhSlug && allNursingHomes) {
      const selectedHomeData = nursingHomes.find(home => home.nh_slug === nhSlug);
      if (selectedHomeData && selectedHomeData.county_slug) {
        setTimeout(() => {
          navigate(`/nursing-homes/${selectedHomeData.county_slug}/${nhSlug}`);
        }, 300); // Small delay to show the selection first
      }
    }
  };

  // Listen for postMessages from the iframe to detect when a marker is clicked
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check if the message is coming from Google Maps
      if (event.origin.includes('google.com')) {
        try {
          // Parse the data if it's in JSON format
          const data = typeof event.data === 'string' 
            ? JSON.parse(event.data) 
            : event.data;
            
          // Check if this is a marker click
          if (data && data.type === 'markerClick' && data.placeId) {
            // Find the nursing home with matching placeId or address
            const home = nursingHomes.find(h => 
              h.place_id === data.placeId || 
              (h.standardized_address && data.title && 
                h.standardized_address.toLowerCase().includes(data.title.toLowerCase()))
            );
            
            if (home) {
              handleHomeSelection(home.nh_slug);
            }
          }
        } catch (e) {
          // Silent fail for parsing errors
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [nursingHomes]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <iframe 
        title={`Map of ${address}`} 
        width="100%" 
        height="100%" 
        style={{ border: 0 }} 
        loading="lazy" 
        src={mapUrl()} 
        allowFullScreen 
        referrerPolicy="no-referrer-when-downgrade" 
        onError={handleMapError}
        onLoad={handleMapLoad}
      ></iframe>
      
      {/* Overlay message when map is loading */}
      {!mapLoaded && (
        <div className="absolute inset-0 bg-gray-100/70 flex items-center justify-center z-10">
          <div className="text-center p-4">
            <div className="animate-spin h-8 w-8 border-2 border-grandpal-green border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-grandpal-gray">Loading map...</p>
          </div>
        </div>
      )}
      
      {/* List of nursing homes as an overlay */}
      {nursingHomes.length > 0 && (
        <NursingHomesList 
          nursingHomes={nursingHomes} 
          countySlug={countySlug}
          selectedHome={selectedHome}
          setSelectedHome={handleHomeSelection}
          defaultExpanded={defaultExpanded}
          navigateOnSelect={allNursingHomes}
        />
      )}
    </div>
  );
}
